import React, { useEffect, useState, useCallback, useContext, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { getEnabledPlugins } from "./registry";
import { PluginContext } from "./context";
import type {
  PluginModule,
  PluginLoadingState,
  PluginContextType,
} from "./types";

// 🚫 ALL FEATURES DISABLED - Uncomment to enable individual features
// Direct imports for all plugins to avoid dynamic import issues
import knowledgePlugin from "../features/knowledge/knowledgebase/features/knowledge/index";
// import pseoPlugin from "../features/pseo/features/pseo/index";
// import aichatPlugin from "../features/aichat/features/aichat/index";
// import crmPlugin from "../features/crm/features/crm/index";
import scopingAiPlugin from "../features/scopingAi/index";
import billValidationPlugin from "../features/billValidation/index";
// import formenginPlugin from "../features/formengine/features/formengine/index";

// Plugin registry with direct imports
const PLUGIN_MODULES: Record<string, PluginModule> = {
  knowledge: knowledgePlugin,
  // pseo: pseoPlugin,
  // aichat: aichatPlugin,
  // crm: crmPlugin,
  scopingAi: scopingAiPlugin,
  billValidation: billValidationPlugin,
  // formengine: formenginPlugin,
};

// Plugin Provider Component
const PluginProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loadedPlugins, setLoadedPlugins] = useState<
    Record<string, PluginModule>
  >({});
  const [loadingStates, setLoadingStates] = useState<
    Record<string, PluginLoadingState>
  >({});
  const [enabledPlugins] = useState<string[]>(getEnabledPlugins());

  const loadPlugin = useCallback(async (pluginName: string) => {
    setLoadingStates((prev) => ({
      ...prev,
      [pluginName]: { pluginName, status: "loading" },
    }));

    try {
      console.log(`🔄 Attempting to load plugin: ${pluginName}`);

      // Use direct imports instead of dynamic imports
      const plugin = PLUGIN_MODULES[pluginName];

      if (!plugin) {
        throw new Error(`Plugin ${pluginName} not found in PLUGIN_MODULES`);
      }

      console.log(`📦 Plugin loaded for ${pluginName}:`, plugin);
      console.log(`✅ Plugin ${pluginName} structure:`, plugin);
      console.log(`✅ Plugin ${pluginName} navigation:`, plugin.navigation);

      // Run plugin initialization if available
      if (plugin.initialize) {
        console.log(`🚀 Initializing plugin: ${pluginName}`);
        await plugin.initialize();
      }

      setLoadedPlugins((prev) => {
        const updated = {
          ...prev,
          [pluginName]: plugin,
        };
        console.log(`📝 Updated loaded plugins:`, Object.keys(updated));
        return updated;
      });

      setLoadingStates((prev) => ({
        ...prev,
        [pluginName]: {
          pluginName,
          status: "loaded",
          module: plugin,
        },
      }));
      console.log(`✅ Successfully loaded plugin: ${pluginName}`);
    } catch (error) {
      console.error(`❌ Failed to load plugin ${pluginName}:`, error);
      setLoadingStates((prev) => ({
        ...prev,
        [pluginName]: {
          pluginName,
          status: "error",
          error: error instanceof Error ? error.message : "Unknown error",
        },
      }));
    }
  }, []);

  useEffect(() => {
    const loadAllPlugins = async () => {
      console.log("🚀 Starting plugin loading process...");
      console.log("🔍 Enabled plugins:", enabledPlugins);

      // Load all enabled plugins
      const results = await Promise.allSettled(
        enabledPlugins.map((pluginName) => loadPlugin(pluginName))
      );

      // Detailed logging of results
      results.forEach((result, index) => {
        const pluginName = enabledPlugins[index];
        if (result.status === "fulfilled") {
          console.log(`✅ Plugin ${pluginName} loaded successfully`);
        } else {
          console.error(
            `❌ Plugin ${pluginName} failed to load:`,
            result.reason
          );
        }
      });

      // Simple check to see if plugins loaded
      const successCount = results.filter(
        (r) => r.status === "fulfilled"
      ).length;
      if (successCount > 0) {
        console.log(
          `✅ Loaded ${successCount}/${enabledPlugins.length} plugins successfully`
        );
        console.log("🔍 Current loaded plugins:", Object.keys(loadedPlugins));
      } else {
        console.error("❌ No plugins loaded successfully");
      }
    };

    loadAllPlugins();
  }, [enabledPlugins, loadPlugin]);

  const isPluginLoaded = useCallback(
    (pluginName: string): boolean => {
      return loadingStates[pluginName]?.status === "loaded";
    },
    [loadingStates]
  );

  const getPluginModule = useCallback(
    (pluginName: string): PluginModule | undefined => {
      return loadedPlugins[pluginName];
    },
    [loadedPlugins]
  );

  const contextValue: PluginContextType = {
    loadedPlugins,
    loadingStates,
    enabledPlugins,
    isPluginLoaded,
    getPluginModule,
  };

  return (
    <PluginContext.Provider value={contextValue}>
      {children}
    </PluginContext.Provider>
  );
};

// Plugin Routes Component
const PluginRoutes: React.FC = () => {
  const { loadedPlugins } = React.useContext(PluginContext)!;
  const [routes, setRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        setIsLoading(true);
        const allRoutes: any[] = [];

        Object.entries(loadedPlugins).forEach(([, plugin]) => {
          if (plugin.routes && Array.isArray(plugin.routes)) {
            allRoutes.push(...plugin.routes);
          }
        });

        setRoutes(allRoutes);
      } catch (err) {
        console.error("Failed to load routes:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadRoutes();
  }, [loadedPlugins]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading plugins...</span>
      </div>
    );
  }

  // 404 Not Found component
  const NotFound = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        Page Not Found
      </h2>
      <p className="text-gray-600 mb-8">
        The page you are looking for doesn't exist or has been moved.
      </p>
      <a
        href="/"
        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
      >
        Go Back Home
      </a>
    </div>
  );

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading page...</span>
      </div>
    }>
      <Routes>
        {routes.map((route, index) => {
          const key = route.path || `plugin-route-${index}`;

          // Handle index routes differently
          if (route.index) {
            return <Route key={key} index={true} element={route.element} />;
          }

          // Handle regular routes
          return <Route key={key} path={route.path} element={route.element} />;
        })}
        {/* 404 fallback for unknown routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

// Plugin Providers Wrapper
export const PluginProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const context = useContext(PluginContext);
  if (!context) return <>{children}</>;
  const { loadedPlugins } = context;

  // Collect all plugin providers
  const providers = Object.values(loadedPlugins)
    .flatMap((plugin) => plugin.providers || [])
    .reverse(); // Reverse to apply in correct order

  // Wrap children in all plugin providers
  return providers.reduce(
    (wrapped, Provider) => <Provider>{wrapped}</Provider>,
    children
  );
};

// Export components and provider
export { PluginProvider, PluginRoutes };
export default PluginProvider;
